import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, ProgressBar } from 'react-bootstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';


const AddForYouCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [video, setVideo] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);



  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/for-you-category/foryou-category`);
        const options = response.data.data.map(category => ({
          value: category.id,
          label: category.name
        }));

        setCategoriesOptions(options);
      } catch (error) {
        toast.error('Failed to load categories');
      }
    };

    fetchCategories();
  }, []);

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnail(file);
    }
  };

  const getPresignedUrl = async (file) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/for-you-category/get-presigned-url`, {
        fileName: file.name,
        fileType: file.type
      });
      return response.data;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      throw new Error('Failed to get upload URL');
    }
  };

  const uploadToS3 = async (url, file, onProgress) => {
    try {
      const contentType =
        file.type && file.type !== ""
          ? file.type
          : file.name.endsWith(".svg")
            ? "image/svg+xml"
            : "application/octet-stream";
      await axios.put(url, file, {
        headers: {
          'Content-Type': contentType,
          "x-amz-server-side-encryption": "aws:kms"
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      });
    } catch (error) {
      console.log("S3 ERROR XML:", error.response?.data);
      throw new Error('Failed to upload file to storage');
    }
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const validateForm = () => {
    if (!title.trim()) {
      toast.error('Title is required.');
      return false;
    }
    if (!video) {
      toast.error('Video is required.');
      return false;
    }
    // Size check can remain, though S3 supports larger files easily.
    // Keeping 200MB limit as per requirement or increasing if needed.
    if (video.size > 500 * 1024 * 1024) { // Increased to 500MB as direct upload is more robust
      toast.error('Video size must not exceed 500MB.');
      return false;
    }
    if (!thumbnail) {
      toast.error('Thumbnail is required.');
      return false;
    }
    if (selectedCategories.length === 0) {
      toast.error('At least one category is required.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }



    setIsLoading(true);
    setIsUploading(true);
    setUploadProgress(0);
    setShowLoadingModal(true);

    try {
      // 1. Get Duration
      const duration = await getVideoDuration(video);

      // 2. Get Presigned URLs
      const [videoPresigned, thumbnailPresigned] = await Promise.all([
        getPresignedUrl(video),
        getPresignedUrl(thumbnail)
      ]);

      // 3. Upload to S3
      // Upload thumbnail first (fast)
      await uploadToS3(thumbnailPresigned.uploadUrl, thumbnail, () => { }); // No progress needed for thumb

      // Upload video with progress
      await uploadToS3(videoPresigned.uploadUrl, video, (progress) => {
        setUploadProgress(progress);
      });

      // 4. Save Metadata to Backend
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/for-you-category/create-foryou-video`;

      const payload = {
        title: title.trim(),
        categories: selectedCategories.map(cat => cat.value),
        videoUrl: videoPresigned.publicUrl,
        thumbnailUrl: thumbnailPresigned.publicUrl,
        duration: duration
      };

      const response = await axios.post(apiUrl, payload);

      if (response.status === 201) {
        setIsLoading(false);
        setIsUploading(false);
        setShowLoadingModal(false);
        setUploadProgress(0);

        toast.success('Video uploaded successfully! 🎉');
        setTimeout(() => {
          navigate('/for-you-video', {
            state: { fromAddPage: true }
          });
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Unexpected response status');
      }

    } catch (error) {
      console.error('Upload flow error:', error);
      setShowLoadingModal(false);
      setIsLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
      toast.error(error.message || 'Failed to upload video. Please try again.');
    }
  };

  const LoadingModal = () => {
    return (
      <Modal
        show={showLoadingModal}
        centered
        backdrop={false}
        keyboard={true}
        size="sm"
      >
        <Modal.Body style={loadingModalStyle}>
          <Spinner animation="border" role="status" style={spinnerStyle} />
          <p style={loadingTextStyle}>Uploading video...</p>
          {uploadProgress > 0 && (
            <div style={{ width: '100%', marginTop: '15px' }}>
              <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
            </div>
          )}
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div style={containerStyle}>
      <LoadingModal />
      <div className="shadow rounded" style={cardStyle}>
        <div style={sectionTitleStyle}>
          <h3 style={titleTextStyle}>Add Video Category</h3>
        </div>
        <div style={userInfoContainer}>
          <div style={profileFieldStyle}>
            <label style={profileLabelStyle}>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={profileInputStyle}
              placeholder="Enter video title"
              maxLength={50}
              required
            />
          </div>

          <div style={profileFieldStyle}>
            <label style={profileLabelStyle}>Thumbnail Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailUpload}
              style={profileInputStyle}
              required
            />
          </div>

          <div style={profileFieldStyle}>
            <label style={profileLabelStyle}>Upload Video</label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setVideo(e.target.files[0])}
              style={profileInputStyle}
              required
            />
          </div>

          <div style={profileFieldStyle}>
            <label style={profileLabelStyle}>Categories</label>
            <Select
              isMulti
              options={categoriesOptions}
              value={selectedCategories}
              onChange={setSelectedCategories}
              placeholder="Select categories"
            />
          </div>
        </div>
      </div>

      <div className="add-btn-align">
        <Button
          type="submit"
          style={buttonStyle}
          onClick={handleSubmit}
          disabled={isLoading || isUploading}
        >
          {isLoading || isUploading ? 'Uploading...' : 'Add'}
        </Button>
      </div>

      {isUploading && !showLoadingModal && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: 'white',
          padding: '15px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1050,
          minWidth: '250px',
        }}>
          <p style={{ margin: '0 0 10px 0', fontWeight: '600', fontSize: '14px' }}>
            Uploading video...
          </p>
          <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
            You can continue browsing
          </p>
        </div>
      )}
    </div>
  );
};

const containerStyle = {
  maxWidth: '90%',
  fontFamily: 'Poppins',
  fontWeight: 600,
};

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  marginBottom: '20px',
  border: 'none',
  width: '50%',
};

const sectionTitleStyle = {
  color: 'white',
  backgroundColor: '#064260',
  padding: '10px',
  borderRadius: '8px 8px 0 0',
  textAlign: 'left',
  fontWeight: 600,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const titleTextStyle = {
  fontSize: '21px',
  margin: 0,
  marginLeft: "30px",
};

const buttonStyle = {
  backgroundColor: '#27B99A',
  color: 'white',
  border: 'none',
  borderRadius: '20px',
  fontSize: '16px',
  cursor: 'pointer',
  width: '15%',
  marginRight: '30px',
};

const userInfoContainer = {
  display: 'flex',
  flexDirection: 'column',
  padding: '20px 40px',
  gap: '20px',
};

const profileFieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const profileLabelStyle = {
  fontWeight: 'bold',
  fontSize: '14px',
};

const profileInputStyle = {
  padding: '8px',
  borderRadius: '4px',
  backgroundColor: '#F4F4F6',
  border: 'none',
  width: '100%',
};

const thumbnailPreviewStyle = {
  marginTop: '10px',
  maxWidth: '100%',
  maxHeight: '200px',
  borderRadius: '4px',
};

const loadingModalStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '30px 20px',
};

const spinnerStyle = {
  color: '#27B99A',
  width: '3rem',
  height: '3rem',
  marginBottom: '15px',
};

const loadingTextStyle = {
  fontSize: '16px',
  fontWeight: '500',
  textAlign: 'center',
  margin: 0,
};

export default AddForYouCategory;